import React from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../components/layout/SiteHeader';
import PageBreadcrumb from '../components/layout/PageBreadcrumb';
import SiteLayout, { SiteMain } from '../components/layout/SiteLayout';
import BrandLogo from '../components/brand/BrandLogo';
import { BRAND_NAME } from '../constants/brand';
import { CONTACT_PATH, HELP_CENTER_PATH } from '../constants/siteNav';

const PILLARS = [
  {
    title: 'Discover events',
    body: 'Concerts, sports, marathons, festivals, conferences, church gatherings, comedy, and more - all in one place.',
    icon: 'fa-compass'
  },
  {
    title: 'Buy with M-Pesa',
    body: 'Secure checkout built for Kenya, with instant ticket delivery to your phone or email.',
    icon: 'fa-mobile-alt'
  },
  {
    title: 'Sell with confidence',
    body: 'Organizers get a simple listing flow, clear payments, and tools to reach their audience.',
    icon: 'fa-calendar-check'
  }
];

const AboutPage = () => {
  return (
    <SiteLayout className="bg-white">
      <SiteHeader />

      <SiteMain>
        <PageBreadcrumb items={[{ label: 'Home', to: '/' }, { label: 'About' }]} />

        {/* Hero - soft wash at top only (Contact-style) */}
        <section className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50/80 overflow-hidden">
          <div className="container mx-auto px-4 py-12 md:py-16 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-10 items-center max-w-6xl mx-auto">
              <div>
                <div className="flex items-center gap-2.5 mb-5">
                  <BrandLogo link={false} size="sm" imgClassName="rounded-md" />
                  <span className="text-sm font-semibold text-gray-700">About {BRAND_NAME}</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-gray-900 leading-tight tracking-tight mb-4">
                  Ticketing made for Kenya
                </h1>
                <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-lg">
                  From marathons and sports to concerts, festivals, conferences, and community
                  events - discover, pay with M-Pesa, and get a ticket that works at the door.
                </p>
              </div>

              <div className="hidden lg:flex justify-end">
                <div className="relative w-72 h-72 xl:w-80 xl:h-80">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-200/40 to-orange-200/30 blur-2xl" />
                  <div className="relative w-full h-full rounded-2xl bg-gradient-to-br from-amber-100/90 to-orange-50 border border-white/70 shadow-xl flex items-center justify-center">
                    <div className="text-center px-6">
                      <div className="w-20 h-20 rounded-full bg-white shadow-lg flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-ticket-alt text-3xl text-orange-500" />
                      </div>
                      <p className="text-sm font-semibold text-gray-800">Sports · Music · Culture</p>
                      <p className="text-xs text-gray-500 mt-1">Festivals · Conferences · Community</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Overlapping card - bridges hero → white */}
        <section className="relative z-10 px-4 -mt-8 md:-mt-10 pb-16">
          <div className="container mx-auto max-w-3xl">
            <div className="bg-white rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.08)] border border-gray-100 p-8 md:p-10">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Why we exist</h2>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-8">
                Event ticketing in Kenya was often scattered across chats, posters, and unreliable
                links. {BRAND_NAME} brings discovery, checkout, and delivery into one trusted flow -
                so attendees get in, and organizers get paid.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/events"
                  className="flex-1 inline-flex items-center justify-center gap-2.5 bg-gray-900 hover:bg-gray-800 text-white px-6 py-4 rounded-lg font-semibold text-sm transition-colors shadow-md"
                >
                  Browse events
                </Link>
                <Link
                  to="/admin/events/create"
                  className="flex-1 inline-flex items-center justify-center gap-2.5 bg-white hover:bg-gray-50 text-gray-900 px-6 py-4 rounded-lg font-semibold text-sm border-2 border-gray-200 hover:border-gray-300 transition-colors"
                >
                  List your event
                </Link>
              </div>
              <p className="text-center text-xs text-gray-400 mt-5 font-medium tracking-wide uppercase">
                Discover · Book · Enjoy
              </p>
            </div>
          </div>
        </section>

        {/* What we do - white ground, left-aligned columns */}
        <section className="bg-gray-50 border-t border-gray-100 py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="max-w-2xl mb-10">
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-2">What we do</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                One platform for finding events, buying tickets, and running sales - across every kind of gathering.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {PILLARS.map((item) => (
                <div key={item.title} className="text-left">
                  <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center mb-4 shadow-sm">
                    <i className={`fas ${item.icon} text-orange-500`} />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Story - same left alignment as sections above */}
        <section className="bg-white border-t border-gray-100 py-16">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-4">Our story</h2>
            <div className="space-y-4 text-sm md:text-base text-gray-600 leading-relaxed">
              <p>
                {BRAND_NAME} started with a practical goal: make it easy to find any event worth
                showing up for - sports meets, marathons, concerts, festivals, conferences,
                worship nights, comedy shows, and community gatherings - pay with M-Pesa, and
                receive a ticket you can actually use at the door.
              </p>
              <p>
                We work with promoters, venues, clubs, churches, brands, and organizers across
                Kenya so every listing feels trustworthy and every sale is tracked.
              </p>
            </div>
          </div>
        </section>

        {/* Help CTA */}
        <section className="bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50/80 border-t border-amber-100/80 py-14">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Questions?</h2>
            <p className="text-sm text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
              Visit the help center or talk to support - we&apos;re here for buyers and organizers.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to={HELP_CENTER_PATH}
                className="inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-6 py-3.5 rounded-lg font-semibold text-sm transition-colors"
              >
                Help center
              </Link>
              <Link
                to={CONTACT_PATH}
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-900 px-6 py-3.5 rounded-lg font-semibold text-sm border-2 border-gray-200 transition-colors"
              >
                Contact us
              </Link>
            </div>
          </div>
        </section>
      </SiteMain>
    </SiteLayout>
  );
};

export default AboutPage;
